import { useState, useEffect, useRef } from 'react';
import { Input, Checkbox, List, Icon, Popup } from "semantic-ui-react";

/**
 * @param {{ placeholder?: string, options: Array<{key: string, text: string, value: string}>, value?: string[], onSelectionChange: (newSelection: string[]) => void }} props
 */
export const SearchableDropdown = ({
  placeholder,
  options,
  value = [],
  onSelectionChange,
}) => {
  const [searchTerm, setSearchTerm] = useState("");

  const [isOpen, setIsOpen] = useState(false);
  const dropdownRef = useRef(null);

  const handleSearchChange = (e) => {
    setSearchTerm(e.target.value);
    setIsOpen(true);
  };

  const handleCheckboxChange = (optionValue) => {
    let newSelectedItems;
    if (value.includes(optionValue)) {
      newSelectedItems = value.filter((item) => item !== optionValue);
    } else {
      newSelectedItems = [...value, optionValue];
    }

    onSelectionChange(newSelectedItems);
  };

  const handleSelectAll = () => {
    if (value?.length === options?.length) {
      onSelectionChange([]);
    } else {
      const allValues = options.map((option) => option.value);

      onSelectionChange(allValues);
    }
    setIsOpen(false); // Close dropdown on select all
  };

  const filteredOptions = options.filter((option) =>
    option.text.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const selectedLabels = options
    .filter((option) => value?.includes(option.value))
    .map((option) => option.text);

  const displayValue =
    isOpen || searchTerm
      ? searchTerm
      : selectedLabels.length > 2
      ? `${selectedLabels.length} items selected`
      : selectedLabels.join(", ");

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsOpen(false);
        setSearchTerm("");
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [setIsOpen, setSearchTerm, dropdownRef]);

  return (
    <div className="w-full relative" ref={dropdownRef}>
      <Popup
        content={
          <List>
            {selectedLabels.map((label, index) => (
              <List.Item key={index} className="border-b border-gray-300">
                {label}
              </List.Item>
            ))}
          </List>
        }
        disabled={selectedLabels?.length <= 2}
        on="hover"
        trigger={
          <div className="w-full">
            <Input
              placeholder={placeholder || "Search..."}
              value={displayValue}
              onChange={handleSearchChange}
              onFocus={() => setIsOpen(true)}
              className="w-[100%] focus:outline-none focus:ring-2 focus:ring-blue-500 "
            />
            <div className="m-auto w-[10%] absolute flex justify-center items-center h-full right-0 top-0">
              <Icon
                name={isOpen ? "chevron up" : "chevron down"}
                className="!cursor-pointer z-5"
                onClick={() => setIsOpen(!isOpen)}
              />
            </div>
          </div>
        }
      />

      {isOpen && (
        <ul className="absolute w-full bg-white border border-gray-300 rounded-md max-h-52 overflow-y-auto z-10 shadow-md">
          <li
            className=" py-2 hover:bg-gray-100 cursor-pointer"
            onClick={handleSelectAll}
          >
            <Checkbox
              label={
                value?.length === options?.length ? "Clear All" : "Select All"
              }
              checked={value?.length === options?.length}
              onChange={handleSelectAll}
              className="mr-2"
            />
          </li>
          {filteredOptions?.length > 0 ? (
            filteredOptions?.map((option) => (
              <li
                key={option.value}
                className=" py-2 hover:bg-gray-100 cursor-pointer"
                onClick={() => handleCheckboxChange(option.value)}
              >
                <Checkbox
                  label={option.text}
                  checked={value.includes(option.value)}
                  onChange={() => handleCheckboxChange(option.value)}
                  className="mr-2"
                />
              </li>
            ))
          ) : (
            <li className=" py-2 text-gray-500">No results found</li>
          )}
        </ul>
      )}
    </div>
  );
};
